import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Upload, FileText, AlertCircle } from 'lucide-react'
import { PageLayout } from '../layout'
import ReviewViewPrototype from './ReviewViewPrototype'

/**
 * Test page for ReviewViewPrototype
 * Allows uploading a CSV file and displaying the strain analysis
 */
export default function ReviewViewPrototypePage({ user, onLogout }) {
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [fileName, setFileName] = useState(null)

  /**
   * Parse CSV file and extract products
   */
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    setLoading(true)
    setError(null)
    setFileName(file.name)

    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const text = e.target.result
        const lines = text.split('\n').filter(line => line.trim())

        if (lines.length < 2) {
          throw new Error('CSV file must contain at least a header row and one data row')
        }

        // Parse header
        const headers = lines[0].split(',').map(h => h.trim())

        // Parse data rows
        const parsedProducts = lines.slice(1).map((line, index) => {
          const values = line.split(',').map(v => v.trim())
          const product = {}

          headers.forEach((header, i) => {
            product[header] = values[i] || ''
          })

          return product
        })

        setProducts(parsedProducts)
        setLoading(false)
      } catch (err) {
        setError(`Failed to parse CSV: ${err.message}`)
        setLoading(false)
      }
    }

    reader.onerror = () => {
      setError('Failed to read file')
      setLoading(false)
    }

    reader.readAsText(file)
  }

  /**
   * Load sample data for testing
   */
  const loadSampleData = () => {
    const sampleProducts = [
      {
        Date: '2025-10-01',
        Customer: 'Test Customer 1',
        Branch: '',
        Strain: 'White',
        'Product Type': 'A Class',
        Quantity: '2.5',
        'Price (£/Kg/Unit)': '8.50'
      },
      {
        Date: '2025-10-01',
        Customer: 'Test Customer 1',
        Branch: '',
        Strain: 'White',
        'Product Type': 'B Class',
        Quantity: '1.0',
        'Price (£/Kg/Unit)': '6.00'
      },
      {
        Date: '2025-10-02',
        Customer: 'Test Customer 2',
        Branch: '',
        Strain: 'Brown',
        'Product Type': 'A Class',
        Quantity: '3.0',
        'Price (£/Kg/Unit)': '9.00'
      },
      {
        Date: '2025-10-02',
        Customer: 'Test Customer 2',
        Branch: '',
        Strain: 'Brown',
        'Product Type': 'B Class',
        Quantity: '2.0',
        'Price (£/Kg/Unit)': '7.00'
      },
      {
        Date: '2025-10-03',
        Customer: 'Test Customer 3',
        Branch: 'Main Branch',
        Strain: 'Oyster',
        'Product Type': 'A Class',
        Quantity: '1.5',
        'Price (£/Kg/Unit)': '8.50'
      },
      {
        Date: '2025-10-03',
        Customer: 'Test Customer 3',
        Branch: 'Main Branch',
        Strain: 'oyster',
        'Product Type': 'A Class',
        Quantity: '2.0',
        'Price (£/Kg/Unit)': '9.00'
      },
      {
        Date: '2025-10-04',
        Customer: 'Test Customer 1',
        Branch: '',
        Strain: 'Oysters',
        'Product Type': 'B Class',
        Quantity: '4.0',
        'Price (£/Kg/Unit)': '8.00'
      },
      {
        Date: '2025-10-04',
        Customer: 'Test Customer 2',
        Branch: '',
        Strain: 'Shiitake',
        'Product Type': 'A Class',
        Quantity: '1.5',
        'Price (£/Kg/Unit)': '12.00'
      },
      {
        Date: '2025-10-05',
        Customer: 'Test Customer 3',
        Branch: 'Main Branch',
        Strain: 'Shiitake',
        'Product Type': 'B Class',
        Quantity: '2.5',
        'Price (£/Kg/Unit)': '10.00'
      },
      {
        Date: '2025-10-05',
        Customer: 'Test Customer 1',
        Branch: '',
        Strain: 'Chestnut',
        'Product Type': 'A Class',
        Quantity: '3.0',
        'Price (£/Kg/Unit)': '7.00'
      }
    ]

    setProducts(sampleProducts)
    setFileName('sample-data.csv')
    setError(null)
  }

  /**
   * Reset the view
   */
  const handleReset = () => {
    setProducts([])
    setFileName(null)
    setError(null)
  }

  return (
    <PageLayout
      title="Review View Prototype"
      subtitle="Interactive strain-based product analysis for Import Wizard V3"
      user={user}
      onLogout={onLogout}
    >
        {/* Header Section */}
        <div className="mb-8">
          {/* Instructions */}
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <strong>Prototype Testing:</strong> Upload a CSV file with columns: Date, Customer, Branch, Strain, Product Type, Quantity, Price (GBP/Kg/Unit)
              {' '}or use the sample data to explore the interface.
            </AlertDescription>
          </Alert>
        </div>

        {/* File Upload Section */}
        {products.length === 0 && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-12">
                <Upload className="w-16 h-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Upload CSV File
                </h3>
                <p className="text-sm text-gray-600 mb-6 text-center max-w-md">
                  Select a CSV file with product data to analyze strain and grade groupings
                </p>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('file-input').click()}
                    disabled={loading}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Choose File
                  </Button>
                  <Button
                    onClick={loadSampleData}
                    disabled={loading}
                  >
                    Load Sample Data
                  </Button>
                </div>

                <input
                  id="file-input"
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Display */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Review View Prototype */}
        {products.length > 0 && (
          <>
            {/* File Info */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FileText className="w-4 h-4" />
                <span>
                  Analyzing: <strong className="text-gray-900">{fileName}</strong>
                  {' '}({products.length} records)
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={handleReset}>
                Upload Different File
              </Button>
            </div>

            {/* Prototype Component */}
            <ReviewViewPrototype products={products} />
          </>
        )}
    </PageLayout>
  )
}
